// frontend/src/lib/lilliPageContext.js
//
// Phase 2C — Lilli page-context bridge. A tiny module-level store that pages/components
// use to tell the floating Lilli chat WHAT the user is currently looking at (selected
// email thread, villa, lead, booking…). LilliChat merges this into every /api/lilli/ask
// request so "this villa" / "this email" resolve to the selected entity.
//
// Deliberately NOT React context: the floating chat lives outside page trees, and pages
// are lazy-loaded — a module singleton + useSyncExternalStore avoids provider plumbing.
// Values are plain serializable data only (ids, names, subjects) — never objects/functions.

import { useSyncExternalStore } from 'react';

let ctx = {};
const listeners = new Set();

function emit() {
  for (const fn of listeners) { try { fn(); } catch { /* listener errors never break the app */ } }
}

// Merge partial context in (e.g. { page:'inbox', selectedThreadId } ). null/undefined
// values delete their key, so callers can unset a single field by passing null.
export function setLilliPageContext(partial) {
  if (!partial || typeof partial !== 'object') return;
  const next = { ...ctx };
  for (const [k, v] of Object.entries(partial)) {
    if (v === null || v === undefined) delete next[k];
    else next[k] = v;
  }
  ctx = next;
  emit();
}

// Clear specific keys, or everything when called with no args.
export function clearLilliPageContext(keys) {
  if (!keys) { ctx = {}; emit(); return; }
  const next = { ...ctx };
  for (const k of keys) delete next[k];
  ctx = next;
  emit();
}

export function getLilliPageContext() {
  return ctx; // treated as immutable — setters always replace the object
}

function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Reactive read for components (e.g. the LilliChat context hint).
export function useLilliPageContext() {
  return useSyncExternalStore(subscribe, getLilliPageContext, getLilliPageContext);
}

// Compact human label for the UI hint ("Casa Yardena" / "Inbox thread" / null).
// No raw ids — luxury-clean.
export function lilliContextLabel(c = ctx) {
  if (c.selectedVillaName) return String(c.selectedVillaName);
  if (c.selectedThreadId) return 'Inbox thread';
  if (c.selectedLeadId) return 'Lead';
  if (c.selectedBookingId) return 'Booking';
  if (c.selectedVillaId) return 'Villa';
  return null;
}
